import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import _get from 'lodash/get';
import { selectors as i18nSelectors, translator } from 'mage-i18n';
import { SpacerHorizontal, SpacerVertical } from '@klarna/bubble-ui';
import {
  HorizontalWrapper,
  formUtils,
  FieldsetForObject,
  defaultContainerPropTypes,
} from 'moet-core';

import { generateCyElemId } from 'moet-test-utils/cypress/utils';

import { CAPS } from '@/constants';
import { CY_QUESTIONNAIRES_CONTAINER_IDS } from '@/constants/questionnaires';
import { BackButton, CallToActionButton, ContainerTemplate } from '@/components';
import { withCapabilityBeforeNext } from '@/flow/common';
import { getGoodsCategories } from '@/selectors/options';
import { getQuestionnaire, preAssessmentDetails } from '@/selectors/merchant/preAssessment';

import Question from './Question';

const tPrefix = 'products-fe.containers.questionnaire';

/*
Usage in flow config:
{
  id: 'questionnaire-whatever',
  path: FLOW_STEP_PATHS.QUESTIONNAIRE_WHATEVER_PATH,
  Container: Questionnaire,
  back: false,
  next: FLOW_STEP_PATHS.ANOTHER_QUESTIONNAIRE_PATH,
  extraProps: { questionnaireIdentifier: 'SEGMENT_ADULT_SHOES_AND_CLOTHING' },
}
*/

export class Questionnaire extends React.Component {
  constructor(props) {
    super(props);
    const { questionnaire = {}, language } = props;
    const { questions = [] } = questionnaire;

    const fields = {};
    questions
      .sort((a, b) => a.order - b.order)
      .forEach(({ text, answer }, index) => {
        fields[index] = {
          component: props => (
            <Question isLast={index === questions.length - 1} text={text} {...props} />
          ),
          size: 1,
          type: 'text',
          label: '',
          validators: [],
          isInitialized: false,
          isValid: false,
          value: answer,
          extraProps: {
            autoFocus: index === 0,
            ...generateCyElemId(CY_QUESTIONNAIRES_CONTAINER_IDS.INPUT_ANSWER(index), window),
          },
        };
      });

    const state = formUtils.initFormFromState({ language, fields });

    this.state = {
      ...state,
      initialState: state,
    };

    this.getBody = this.getBody.bind(this);
    this.onChangeFields = this.onChangeFields.bind(this);
    this.onNext = this.onNext.bind(this);
  }

  onChangeFields(fields) {
    this.setState({ fields });
  }

  getBody() {
    const { questionnaires = {} } = this.props.preAssessmentDetails;

    const { questionnaireIdentifier, questionnaire = {} } = this.props;

    const body = {
      questionnaires: {
        ...questionnaires,
        [questionnaireIdentifier]: {
          title: questionnaire.title,
          subtitle: questionnaire.subtitle,
          questions: Object.values(this.state.fields).map(({ id, value: answer }, index) => {
            const question = _get(questionnaire, `questions[${index}]`);

            return { ...question, answer };
          }),
        },
      },
    };

    return body;
  }

  onNext() {
    this.props.onNext({
      payload: {
        params: { mid: this.props.mid },
        body: this.getBody(),
      },
      hasChanged: true,
    });
  }

  render() {
    const {
      error,
      hasBack,
      isLoading,
      isDisabled = false,
      onBack,
      questionnaire = {},
      questionnaireIdentifier,
      t,
    } = this.props;
    const { fields } = this.state;
    const { title = '', subtitle = '' } = questionnaire;

    const tabIndex = Object.keys(fields).length + 1;

    return (
      <ContainerTemplate title={title} subtitle={subtitle} error={error}>
        <SpacerVertical spaceToken="space/150" />
        <FieldsetForObject
          name={questionnaireIdentifier}
          isBorderless
          isDisabled={this.props.isDisabled}
          fields={fields}
          onBlur={key => formUtils.onBlurFields(key, this.state, this.onChangeFields)}
          onChange={(key, val) =>
            formUtils.onChangeFields(key, val, this.state, this.onChangeFields)
          }
        />
        <SpacerVertical spaceToken="space/600" />
        <HorizontalWrapper display="inline-flex" alignItems="center" justifyContent="flex-end">
          {hasBack && (
            <>
              <BackButton
                tabIndex={tabIndex + 1}
                onClick={onBack}
                disabled={isDisabled}
                {...generateCyElemId(CY_QUESTIONNAIRES_CONTAINER_IDS.BTN_BACK, window)}
              >
                {t(`${tPrefix}.buttons.back`)}
              </BackButton>
              <SpacerHorizontal spaceToken="space/300" />
            </>
          )}
          <CallToActionButton
            tabIndex={tabIndex}
            onClick={this.onNext}
            loading={isLoading}
            disabled={isDisabled}
            {...generateCyElemId(CY_QUESTIONNAIRES_CONTAINER_IDS.BTN_SUBMIT, window)}
          >
            {t(`${tPrefix}.buttons.next`)}
          </CallToActionButton>
        </HorizontalWrapper>
      </ContainerTemplate>
    );
  }
}

Questionnaire.propTypes = {
  ...defaultContainerPropTypes,
  language: PropTypes.string.isRequired,
  mid: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  goodsCategories: PropTypes.array,
};

export default compose(
  connect((state, { questionnaireIdentifier }) => ({
    goodsCategories: getGoodsCategories(state),
    language: i18nSelectors.language(state),
    preAssessmentDetails: preAssessmentDetails(state),
    questionnaire: getQuestionnaire(questionnaireIdentifier)(state),
    t: translator(state),
  })),
  withCapabilityBeforeNext({ cap: CAPS.V2_PRE_ASSESSMENT_PATCH }),
)(Questionnaire);
